import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconCirclePlus, IconX, IconUpload, IconBellRinging, } from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSingleCustomerAccountDetail, clearSingleUser } from "../../redux/slices/usersSlice";
import { updateCustomerAccountDetail, registerNewAccount } from "../../redux/slices/usersSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

function NavItem({ label, to }) {
  return (
    <a href={to} className="px-3 py-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-white/10">
      <span>{label}</span>
    </a>
  );
}

const UserAccountForm = ({ userData = null }) => {
  // Initialize form data based on whether we're creating or editing
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const { singleUser, singleUserLoading, error, loading } = useSelector((state) => state.users);

  const isNewUser = id === "new";

  useEffect(() => {
    if (!isNewUser) {
      dispatch(fetchSingleCustomerAccountDetail(id));
    }
    setLayoutConfig({
      title: `${isNewUser ? 'User Account / Add' : 'User Account / Edit'}`,
      showBackButton: true,
      scrollable: true,
    });
    return () => {
      dispatch(clearSingleUser());
    };
  }, [dispatch, id, isNewUser, setLayoutConfig]);

  const [formData, setFormData] = useState({
    customer_full_name: userData?.full_name || '',
    email: userData?.email || '',
    phone_number: userData?.phone_number || '',
    profilePicture: null,
    account_status: userData?.account_status || 'Active'
  });

  useEffect(() => {
    if (singleUser && !isNewUser) {
      setFormData({
        customer_full_name: singleUser.full_name || '',
        email: singleUser.email || '',
        phone_number: singleUser.phone_number || '',
        profilePicture: null,
        account_status: singleUser.account_status || 'active',
      });
      setPreviewImage(singleUser.profile_picture || null);
    }
  }, [singleUser, isNewUser]);

  const [previewImage, setPreviewImage] = useState(userData?.profile_picture_url || null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, profilePicture: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isNewUser) {
      const formDataToSend = new FormData();
      formDataToSend.append("customer_full_name", formData.customer_full_name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone_number", formData.phone_number);
      if (formData.profilePicture) {
        formDataToSend.append("profile_picture", formData.profilePicture);
      }
      formDataToSend.append("account_status", formData.account_status);

      try {
        await dispatch(updateCustomerAccountDetail({ userId: id, formData: formDataToSend })).unwrap();
        navigate('/admin/users');

      } catch (error) {
        console.error("Failed to update user:", error);
      }
    } else {
      const formDataToSend = new FormData();
      formDataToSend.append("full_name", formData.customer_full_name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone_number", formData.phone_number);
      if (formData.profilePicture) {
        formDataToSend.append("profile_picture", formData.profilePicture);
      }
      formDataToSend.append("user_account_type", 'customer');

      try {
        await dispatch(registerNewAccount(formDataToSend)).unwrap();
        navigate('/admin/users');
      } catch (error) {
        console.error("Failed to create user:", error);
      }
    }
  };

  if (loading) {
    return <LoadingFallback />;
  }
  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(fetchSingleCustomerAccountDetail(id))} />;
  }

  return (
    <div>
      <main className="flex-1 overflow-y-auto shadow-lg">
        <div className="border-2 border rounded-md bg-[#FFFFFF] p-6 shadow-lg">
          <form onSubmit={handleSubmit}>
            {/* Profile Picture Upload */}
            <div className="mb-6 flex flex-col items-center">
              <div className="relative w-32 h-32 mb-4">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="Profile Preview"
                    className="w-full h-full rounded-full object-cover border-2 border-[#463BC9]"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-gray-200 border-2 border-[#463BC9] flex items-center justify-center">
                    <span className="text-gray-500">No Image</span>
                  </div>
                )}
              </div>
              <label className="cursor-pointer bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90 flex items-center gap-2">
                <IconUpload size={16} />
                Upload Photo
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>
            </div>

            {/* Basic Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#0B0C52] mb-4 border-b-2 border-[#463BC9] pb-2">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0B0C52] mb-1">Full Name</label>
                  <input
                    type="text"
                    name="customer_full_name"
                    value={formData.customer_full_name}
                    onChange={handleChange}
                    placeholder="Enter Full Name"
                    className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0B0C52] mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Email"
                    className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0B0C52] mb-1">Phone Number</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                    className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                  />
                </div>
              </div>
            </div>

            {/* Status Section (only shown when editing) */}
            {!isNewUser && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0B0C52] mb-4 border-b-2 border-[#463BC9] pb-2">
                    Account Status
                  </h3>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="account_status"
                        value="active"
                        checked={formData.account_status === 'active'}
                        onChange={handleChange}
                        className="h-4 w-4 text-[#463BC9] focus:ring-[#463BC9]"
                      />
                      <span className="text-sm text-[#0B0C52]">Active</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="account_status"
                        value="blocked"
                        checked={formData.account_status === 'blocked'}
                        onChange={handleChange}
                        className="h-4 w-4 text-[#463BC9] focus:ring-[#463BC9]"
                      />
                      <span className="text-sm text-[#0B0C52]">Blocked</span>
                    </label>
                  </div>
                </div>

                {/* Registration Date and Last Login (Read-Only) */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0B0C52] mb-4 border-b-2 border-[#463BC9] pb-2">
                    Account Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0B0C52] mb-1">Registration Date</label>
                      <input
                        type="text"
                        value={singleUser?.registration_date ? new Date(singleUser.registration_date).toLocaleString() : ''}
                        readOnly
                        className="border-2 border-gray-300 w-full rounded px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0B0C52] mb-1">Last Login</label>
                      <input
                        type="text"
                        value={singleUser?.last_login ? new Date(singleUser.last_login).toLocaleString() : ''}
                        readOnly
                        className="border-2 border-gray-300 w-full rounded px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-4 border-t border-[#463BC9]">
              <button
                type="submit"
                className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90 flex items-center gap-1"
              >
                <IconCirclePlus size={18} />
                {isNewUser ? 'Add User' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default UserAccountForm;